import { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import ModalCloseIcon from "../../../Assets/svg/close-modal.svg";
import ticketLogo from "../../../Assets/svg/ticket_icon.svg";
import SearchWithAPI from "../../common/searchbox/searchbox.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from "react-icons/fi";
import moment from "moment";
import { apiCall } from "../../../services/api-service.js";
import { getEnabledFieldApi } from "../Ticketapi.js";

const EditMainInfoModal = ({ ticketDetail, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [levelOptionList, setLevelOptionList] = useState([{ value: "", label: "" }]);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [estimation, setEstimation] = useState("");
  const [expectedDate, setExpectedDate] = useState(new Date());
  const [lastKey, setLastKey] = useState("");
  const [enabledFields, setEnabledFields] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await getEnabledFieldApi();
        if (response?.success) {
          setEnabledFields(response?.enabled_fields ?? []);
          setMandatoryFields(response?.mandatory_fields ?? []);
          setFieldLabels(response?.application_labels ?? response?.labels ?? {});
        }
      } catch (err) {
        console.error("Error fetching field labels:", err);
      }
    };
    fetchLabels();
  }, []);

  const isFieldEnabled = (field) => {
    if (field === "Title" || field === "Project") return true;
    if (field === "Customer" || field === "Client Name") {
      return enabledFields.some((item) => item === "Customer" || item === "Client Name");
    }
    return enabledFields.some((item) => item === field);
  };

  const isFieldMandatory = (field) => {
    if (field === "Title" || field === "Project") return true;
    if (!isFieldEnabled(field)) return false;
    if (field === "Customer" || field === "Client Name") {
      return mandatoryFields.some((item) => item === "Customer" || item === "Client Name");
    }
    return mandatoryFields.some((item) => item === field);
  };

  const getFieldLabel = (fieldKey, defaultLabel = fieldKey) => {
    let custom = fieldLabels?.[fieldKey];
    if (custom && typeof custom === "string" && custom.trim() !== "") {
      return custom.trim();
    }
    return defaultLabel;
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await apiCall("get", `ticket/edit/${ticketDetail.id}`);
        if (response?.data?.success) {
          const t = response.data.ticket;
          setSelectedProject({ value: t.project_id, label: t.project });

          const tempLevels = t.levels?.length > 0 ? [...t.levels] : [];
          tempLevels.push({ value: "", label: "" });
          setLevelOptionList(tempLevels);

          setSelectedAssignee(t.assignee_id ? { value: t.assignee_id, label: t.assignee } : null);
          setEstimation(t.est || "");
          setExpectedDate(t.expected ? new Date(t.expected) : new Date());
        }
      } catch (err) {
        console.error("Fetch Details Error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (ticketDetail?.id) {
      fetchDetails();
    }
  }, [ticketDetail]);

  const handleLevelListDropdownChange = (option, onChangeIndex) => {
    const idx = onChangeIndex?.parentIndex ?? 0;
    const tempList = [...levelOptionList].slice(0, idx);

    if (option && option.value) {
      tempList.push(option);
      tempList.push({ value: "", label: "" });
    } else {
      tempList.push({ value: "", label: "" });
    }

    setLevelOptionList(tempList);
    setErrors((prev) => ({ ...prev, component_id: "" }));
  };

  const getLevelParams = (index) => {
    if (index === 0) {
      return { project_id: selectedProject?.value || "" };
    }
    return {
      parent_id: levelOptionList[index - 1]?.value || "",
      project_id: selectedProject?.value || "",
    };
  };
  const handleEstimationChange = (event) => {
    const inputText = event.target.value;
    if (/^\d{0,3}(:\d{0,2})?$/.test(inputText)) {
      if (lastKey === "Backspace") {
        setEstimation(inputText);
      } else {
        setEstimation(inputText.length === 2 && !inputText.includes(":") ? inputText + ":" : inputText);
      }
      setErrors((prev) => ({ ...prev, estimation: "" }));
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!selectedProject) newErrors.project_id = "Project is required";
    if (isFieldEnabled("Level") && isFieldMandatory("Level") && (!levelOptionList[0] || levelOptionList[0].value === "")) {
      newErrors.component_id = "Component is required";
    }
    if (isFieldMandatory("Assign To") && !selectedAssignee) newErrors.assign_to = "Assignee is required";
    if (isFieldMandatory("Estimation") && (!estimation || !/^([0-9]+):([0-5]?[0-9])$/.test(estimation))) newErrors.estimation = "Format hh:mm required";
    if (isFieldMandatory("Expected Date") && !expectedDate) newErrors.expected_date = "Expected date is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const validLevels = levelOptionList.filter((item) => item.value !== "");
    const payload = {
      project_id: selectedProject.value,
      component_id: validLevels[0]?.value,
      sub_component_id: validLevels[1]?.value || null,
      assign_to: selectedAssignee.value,
      estimation: estimation,
      expected_date: moment(expectedDate).format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
      const response = await apiCall("put", `ticket/update_main_info/${ticketDetail.id}`, payload);
      if (response?.data?.success) {
        onSuccess();
      } else {
        setErrors(response?.data?.error || { message: response?.data?.message });
      }
    } catch (err) {
      console.error("Save Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      borderRadius: "24px",
      width: "520px",
      padding: "24px",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
      maxHeight: "90vh",
      overflowY: "auto",
    },
  };

  const CustomDateInput = ({ value, onClick }) => (
    <div style={{ position: "relative", width: "100%" }} onClick={onClick}>
      <input
        value={value}
        readOnly
        placeholder="Select date"
        style={{
          width: "100%",
          height: "40px",
          padding: "10px 40px 10px 12px",
          border: "1px solid #EBEBEB",
          borderRadius: "8px",
          fontSize: "14px",
          background: "#FFFFFF",
          cursor: "pointer",
        }}
      />
      <FiCalendar
        style={{
          position: "absolute",
          right: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#8790A1",
          fontSize: "16px",
        }}
      />
    </div>
  );

  return (
    <div style={modalStyle.overlay} onClick={onHide}>
      <div style={modalStyle.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#212129", margin: 0 }}>Edit Main Info</h2>
          <div
            onClick={onHide}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              border: "1px solid #EBEBEB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <img src={ModalCloseIcon} alt="close" style={{ width: "14px" }} />
          </div>
        </div>

        {/* Ticket Info Card */}
        <div
          style={{
            background: "#F7F8F9",
            borderRadius: "16px",
            padding: "16px",
            display: "flex",
            gap: "16px",
            alignItems: "flex-start",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              background: "#ffff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img src={ticketLogo} alt="ticket" style={{ width: "20px" }} />
          </div>
          <div style={{ overflow: "hidden" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#212129",
                marginBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {ticketDetail.code} - {ticketDetail.title}
            </h3>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "14px", color: "#8790A1" }}>{ticketDetail.project || "No Project"}</span>
              <span style={{ width: "4px", height: "4px", background: "#D9D9D9", borderRadius: "50%" }}></span>
              <span style={{ fontSize: "14px", color: "#6929E2", fontWeight: "500" }}>{ticketDetail.status}</span>
            </div>
          </div>
        </div>

        {/* Form Grid */}
        <div className="row g-3">
          <div className="col-6">
            <label className="form-label" style={{ fontSize: "14px", fontWeight: "600" }}>
              {getFieldLabel("Project", "Project")} <span className="text-danger">*</span>
            </label>
            <SearchWithAPI
              apiUrl="get_project"
              onDropdownChange={(opt) => {
                setSelectedProject(opt);
                setLevelOptionList([{ value: "", label: "" }]);
                setErrors((prev) => ({ ...prev, project_id: "" }));
              }}
              selectedOption={selectedProject}
              placeholder="Select"
            />
            {errors.project_id && <small className="text-danger">{errors.project_id}</small>}
          </div>
          {isFieldEnabled("Level") && levelOptionList.map((levelOption, index) => (
            <div className="col-6" key={index}>
              <label className="form-label" style={{ fontSize: "14px", fontWeight: "600" }}>
                {getFieldLabel("Level", "Level")} {index + 1} {index === 0 && isFieldMandatory("Level") && <span className="text-danger">*</span>}
              </label>
              <SearchWithAPI searchId={index} onChangeIndex={{ parentIndex: index }} onDropdownChange={handleLevelListDropdownChange} apiUrl={index === 0 ? "get_parent_component" : "get_child_component"} params={getLevelParams(index)} placeholder="Select" selectedOption={levelOption} />
              {index === 0 && errors.component_id && <small className="text-danger">{errors.component_id}</small>}
            </div>
          ))}
          <div className="col-12">
            <label className="form-label" style={{ fontSize: "14px", fontWeight: "600" }}>
              {getFieldLabel("Assign To", "Assign To")} <span className="text-danger">*</span>
            </label>
            <SearchWithAPI
              apiUrl="get_employee"
              onDropdownChange={(opt) => {
                setSelectedAssignee(opt);
                setErrors((prev) => ({ ...prev, assign_to: "" }));
              }}
              selectedOption={selectedAssignee}
              placeholder="Select"
            />
            {errors.assign_to && <small className="text-danger">{errors.assign_to}</small>}
          </div>
        </div>

        {/* Estimation & Date Row */}
        <div
          style={{
            background: "#F7F8F9",
            borderRadius: "16px",
            padding: "20px",
            marginTop: "24px",
            marginBottom: "24px",
            display: "flex",
            gap: "20px",
          }}
        >
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", display: "block" }}>
              {getFieldLabel("Estimation", "Estimation")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="hh:mm"
              value={estimation}
              onKeyDown={(e) => setLastKey(e.key)}
              onChange={handleEstimationChange}
              style={{
                height: "40px",
                borderRadius: "8px",
                border: "1px solid #EBEBEB",
                fontSize: "14px",
              }}
            />
            {errors.estimation && <small className="text-danger">{errors.estimation}</small>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", display: "block" }}>
              {getFieldLabel("Expected Date", "Expected date")} <span className="text-danger">*</span>
            </label>
            <DatePicker
              selected={expectedDate}
              onChange={(date) => {
                setExpectedDate(date);
                setErrors((prev) => ({ ...prev, expected_date: "" }));
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="time"
              popperClassName="expected-date-popper"
              portalId="portal-root"
              customInput={<CustomDateInput />}
              dateFormat="MMM dd, yyyy h:mm aa"
            />
            {errors.expected_date && <small className="text-danger">{errors.expected_date}</small>}
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-3">
          <button
            onClick={onHide}
            style={{
              padding: "12px 32px",
              borderRadius: "100px",
              border: "1px solid #EBEBEB",
              background: "#FFFFFF",
              fontSize: "16px",
              fontWeight: "700",
              color: "#212129",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: "12px 32px",
              borderRadius: "100px",
              border: "none",
              background: "#6929E2",
              fontSize: "16px",
              fontWeight: "700",
              color: "#FFFFFF",
              cursor: "pointer",
              minWidth: "120px",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? <Spinner animation="border" size="sm" variant="light" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMainInfoModal;
